import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [status, setStatus] = useState("checking"); // checking | paid | error
  const attempts = useRef(0);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let active = true;
    const poll = async () => {
      if (!active) return;
      if (attempts.current >= 8) {
        setStatus("error");
        return;
      }
      attempts.current += 1;
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          await checkAuth();
          setStatus("paid");
          return;
        }
      } catch (e) {
        console.error("Payment status poll failed, retrying:", e);
        // keep polling
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { active = false; };
  }, [params, checkAuth]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-white p-8 text-center">
      {status === "checking" && (
        <div data-testid="payment-checking">
          <Loader2 className="h-10 w-10 animate-spin text-[#003366] mx-auto" />
          <p className="mt-5 text-slate-500">Confirming your subscription…</p>
        </div>
      )}
      {status === "paid" && (
        <div data-testid="payment-success">
          <CheckCircle2 className="h-14 w-14 text-[#003366] mx-auto" />
          <h1 className="font-display font-extrabold text-3xl tracking-tight text-slate-900 mt-6">Welcome to Premium</h1>
          <p className="text-slate-500 mt-2">You now have unlimited folders, decks, and flashcards.</p>
          <Button data-testid="back-to-app-btn" onClick={() => navigate("/app")} className="mt-8 rounded-full h-11 px-8 bg-[#003366] hover:bg-[#00284d] text-white">
            Go to Revora
          </Button>
        </div>
      )}
      {status === "error" && (
        <div data-testid="payment-error">
          <XCircle className="h-14 w-14 text-slate-400 mx-auto" />
          <h1 className="font-display font-bold text-2xl tracking-tight text-slate-900 mt-6">We couldn't confirm payment</h1>
          <p className="text-slate-500 mt-2">If you completed checkout, your plan will update shortly.</p>
          <Button onClick={() => navigate("/app")} className="mt-8 rounded-full h-11 px-8 bg-[#003366] hover:bg-[#00284d] text-white">
            Back to Revora
          </Button>
        </div>
      )}
    </div>
  );
}

export function PaymentCancel() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-white p-8 text-center" data-testid="payment-cancel">
      <XCircle className="h-14 w-14 text-slate-400 mx-auto" />
      <h1 className="font-display font-bold text-2xl tracking-tight text-slate-900 mt-6">Checkout cancelled</h1>
      <p className="text-slate-500 mt-2">No charge was made. You can upgrade anytime.</p>
      <Button onClick={() => navigate("/app")} className="mt-8 rounded-full h-11 px-8 bg-[#003366] hover:bg-[#00284d] text-white">
        Back to Revora
      </Button>
    </div>
  );
}
